import { makeStyles, Typography, useTheme } from '@material-ui/core'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { LeadAnnouncementResponseModel } from '../../../../api/Argentia/endpoints/leads/model/leads'
import { EnumTipoLead } from '../../../../api/oData/enums/enumTipoLead'
import AnnouncementList from '../../../../components/shared/announcementList/announcementList'
import { AnnouncementListItemModel } from '../../../../components/shared/announcementListItem/announcementListItem.model'
import { CardActionProps } from '../../../../components/shared/cardActions/cardActions'
import { LandingKpiCard } from '../../../../components/shared/landingKpiCard/landingKpiCard'
import { enumToMapValues, MapToArray } from '../../../../helpers'
import { EnumLeadAnnouncementTypes } from '../../leads.model'
import { leadsStyle } from './leadsKpiCard.styles'

interface LeadsKpiCardProps {
  leadType: EnumTipoLead
  isFetchingAnnouncements?: boolean
  isFetchingKpiItems?: boolean
  announcementsList: LeadAnnouncementResponseModel[]
  onAnnouncementViewClick: (itemTypeValue: number) => void
  onSearchClickHandler: () => void
  onPreciosClickHandler: () => void
  onLatestClickHandler: () => void
  onAddLead: () => void
}

export const LeadsKpiCard: React.FC<LeadsKpiCardProps> = ({
  leadType,
  isFetchingAnnouncements = false,
  isFetchingKpiItems = false,
  announcementsList,
  onAnnouncementViewClick,
  onSearchClickHandler,
  onPreciosClickHandler,
  onLatestClickHandler,
  onAddLead
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'leads'])
  const currentTheme = useTheme()
  const useStyles = makeStyles(leadsStyle(currentTheme))
  const classes = useStyles()

  const leadTypeIsPrecios = leadType === EnumTipoLead.Precios
  const announcementTypesMap = MapToArray(enumToMapValues(EnumLeadAnnouncementTypes))
  const hasDraftQuotesQty = announcementsList.filter(item => item.hasDraftQuote).length
  const hasNotCurrentQuotesQty = announcementsList.filter(item => item.hasNotCurrentQuotes).length
  const hasRejectedQuotesQty = announcementsList.filter(item => item.hasRejectedQuotes).length

  // #region Handlers Starts
  const footerActions: CardActionProps[] = [
    {
      label: t('leads:components.kpiCard.actions.search'),
      handleActionClick: onSearchClickHandler,
      hasPermission: true,
      color: 'secondary'
    },
    ...(leadTypeIsPrecios
      ? [
          {
            label: t('leads:components.kpiCard.actions.prices'),
            handleActionClick: onPreciosClickHandler,
            hasPermission: true
          }
        ]
      : []),
    {
      label: t('leads:components.kpiCard.actions.latest'),
      handleActionClick: onLatestClickHandler,
      hasPermission: true
    },
    {
      label: t('leads:components.kpiCard.actions.addLead'),
      handleActionClick: onAddLead,
      hasPermission: true,
      variant: 'contained'
    }
  ]
  // #endregion Handlers Ends

  // #region Functions Starts

  const getItemTypeQuantity = (itemType: EnumLeadAnnouncementTypes) => {
    let result: number = 0
    switch (itemType) {
      case EnumLeadAnnouncementTypes.HAS_DRAFT_QUOTE:
        result = hasDraftQuotesQty
        break
      case EnumLeadAnnouncementTypes.HAS_NOT_CURRENT_QUOTES:
        result = hasNotCurrentQuotesQty
        break
      case EnumLeadAnnouncementTypes.HAS_REJECTED_QUOTES:
        result = hasRejectedQuotesQty
        break
      default:
        result = 0
    }
    return result
  }

  const listItemArray: AnnouncementListItemModel[] = announcementTypesMap
    .map(itemType => {
      const itemTypeQty = getItemTypeQuantity(itemType.value)
      if (
        itemType.value === EnumLeadAnnouncementTypes.HAS_REJECTED_QUOTES &&
        leadType !== EnumTipoLead.Personalizado
      ) {
        return undefined
      }

      // EnumTipoLead.Precios must not show any message. Hence, this shows emptyMessage (is used as generic message)
      if (
        itemType.value === EnumLeadAnnouncementTypes.GENERIC_EMPTY &&
        leadType !== EnumTipoLead.Precios
      ) {
        return undefined
      }
      if (
        leadType === EnumTipoLead.Precios &&
        itemType.value !== EnumLeadAnnouncementTypes.GENERIC_EMPTY
      ) {
        return undefined
      }

      return {
        emptyMessage: t(
          `leads:components.kpiCard.announcementListItems.${itemType.name}.emptyMessage`
        ),
        listIsEmpty: itemTypeQty === 0,
        avatarText: t(`leads:components.kpiCard.announcementListItems.${itemType.name}.avatarText`),
        primaryText: t(
          `leads:components.kpiCard.announcementListItems.${itemType.name}.primaryText`,
          { count: itemTypeQty }
        ),
        secondaryText: t(
          `leads:components.kpiCard.announcementListItems.${itemType.name}.secondaryText`
        ),
        actionText: t(`leads:components.kpiCard.announcementListItems.${itemType.name}.actionText`),
        hasPermission: true,
        itemType: itemType.value,
        onClickHandler: (itemTypeValue: number) => {
          onAnnouncementViewClick(itemTypeValue)
        }
      }
    })
    .filter(item => item !== undefined)
  // #endregion Functions Ends

  // #region Form Definition Starts
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Hooks Starts
  // #endregion Hooks Ends

  return (
    <LandingKpiCard
      isFetchingLeftContent={isFetchingKpiItems}
      leftTitle={t('leads:components.kpiCard.kpiItems.title')}
      leftContent={<Typography></Typography>}
      isFetchingRightContent={isFetchingAnnouncements}
      rightTitle={t('leads:components.kpiCard.announcementListItems.title')}
      rightContent={<AnnouncementList announcementItems={listItemArray} />}
      footerActions={footerActions}
    />
  )
}
